// Named states of the prototype. The control panel's "Jump to" list and the URL hash use them,
// so every screen has a stable link (e.g. #layout=duoInner&mode=dark&screen=chat-group).

import {
  initialState,
  setState,
  getState,
  type Layout,
  type Mode,
  type State,
  type Tab,
  type Route,
  type AuthStage,
  type Onboarding,
  emptyOnboarding,
} from '../state/store';
import { initialChats } from '../data/mock';

export interface Preset {
  id: string;
  label: string;
  group: 'Sign-in' | 'Chats' | 'Calls' | 'Feed' | 'You';
  apply: () => Partial<State>;
}

const signedIn = (tab: Tab, stack: Route[] = [], extra: Partial<State> = {}): Partial<State> => {
  const s = getState();
  // Keep a signed-in user without an organisation in that state for screens that exist for them;
  // everything else (Feed, organisation chats) needs an organisation.
  const guestOk =
    s.auth.stage === 'in' &&
    s.orgId === null &&
    stack.every((r) => r.name === 'settings') &&
    tab !== 'feed' &&
    !extra.call;
  return {
    auth: {
      stage: 'in',
      email: guestOk
        ? s.auth.email
        : s.auth.email.includes('@nordwind') || s.auth.email.includes('@praxis')
          ? s.auth.email
          : 'anna.berg@nordwind-logistik.de',
    },
    orgId: guestOk ? null : (s.orgId ?? 'nordwind'),
    tab,
    stacks: { ...initialState.stacks, [tab]: stack },
    call: null,
    composeOpen: false,
    chatUi: {},
    ...extra,
  };
};

const fresh = () => ({
  chats: initialChats,
  requests: initialState.requests,
  posts: initialState.posts,
});

const onboardingAt = (stage: AuthStage, onboarding: Partial<Onboarding>): Partial<State> => ({
  auth: { stage, email: 'anna.berg@nordwind-logistik.de' },
  onboarding: { ...emptyOnboarding, ...onboarding },
  orgId: null,
  call: null,
  composeOpen: false,
});

export const presets: Preset[] = [
  {
    id: 'signin-welcome',
    label: 'Welcome',
    group: 'Sign-in',
    apply: () => ({
      auth: { stage: 'welcome', email: '' },
      onboarding: emptyOnboarding,
      orgId: null,
      call: null,
      composeOpen: false,
    }),
  },
  {
    id: 'signin-email',
    label: 'Email',
    group: 'Sign-in',
    apply: () => ({
      auth: { stage: 'email', email: 'anna.berg@nordwind-logistik.de' },
      onboarding: emptyOnboarding,
      orgId: null,
      call: null,
    }),
  },
  {
    id: 'signin-code',
    label: 'Email code',
    group: 'Sign-in',
    apply: () => ({
      auth: { stage: 'code', email: 'anna.berg@nordwind-logistik.de' },
      onboarding: emptyOnboarding,
      orgId: null,
      call: null,
    }),
  },
  {
    id: 'signin-phone',
    label: 'Phone',
    group: 'Sign-in',
    apply: () => onboardingAt('phone', { phone: '151 23456789' }),
  },
  {
    id: 'signin-sms',
    label: 'SMS code',
    group: 'Sign-in',
    apply: () => onboardingAt('sms', { phone: '151 23456789' }),
  },
  {
    id: 'signin-terms',
    label: 'Terms',
    group: 'Sign-in',
    apply: () => onboardingAt('terms', { phone: '151 23456789', terms: true }),
  },
  {
    id: 'signin-profile',
    label: 'Profile',
    group: 'Sign-in',
    apply: () =>
      onboardingAt('profile', {
        phone: '151 23456789',
        terms: true,
        privacy: true,
        name: 'Anna Berg',
        title: 'Head of Dispatch',
        photo: true,
      }),
  },
  {
    id: 'signin-unlock',
    label: 'Face ID',
    group: 'Sign-in',
    apply: () =>
      onboardingAt('unlock', {
        phone: '151 23456789',
        terms: true,
        privacy: true,
        name: 'Anna Berg',
        title: 'Head of Dispatch',
        photo: true,
      }),
  },
  {
    id: 'signin-unlock-optional',
    label: 'Face ID (optional)',
    group: 'Sign-in',
    apply: () => ({
      ...onboardingAt('unlock', {
        phone: '151 23456789',
        terms: true,
        privacy: true,
        name: 'Miriam Falk',
        title: 'Occupational physician',
      }),
      auth: { stage: 'unlock', email: 'm.falk@praxis-am-park.de' },
    }),
  },
  {
    id: 'signin-branding',
    label: 'Branding loads',
    group: 'Sign-in',
    apply: () => {
      const s = getState();
      const email =
        s.orgId === 'praxis'
          ? 'm.falk@praxis-am-park.de'
          : s.orgId === null && s.auth.stage === 'in'
            ? 'paul.schreiber@example.com'
            : 'anna.berg@nordwind-logistik.de';
      return { auth: { stage: 'branding', email }, orgId: null, call: null };
    },
  },
  {
    id: 'chats',
    label: 'Chat list',
    group: 'Chats',
    apply: () => ({ ...signedIn('chats'), ...fresh() }),
  },
  {
    id: 'requests',
    label: 'Chat requests',
    group: 'Chats',
    apply: () => signedIn('chats', [{ name: 'requests' }]),
  },
  {
    id: 'chat-direct',
    label: 'One-to-one chat',
    group: 'Chats',
    apply: () => signedIn('chats', [{ name: 'chat', id: 'jonas' }]),
  },
  {
    id: 'chat-group',
    label: 'Group chat',
    group: 'Chats',
    apply: () => signedIn('chats', [{ name: 'chat', id: 'dispatch' }]),
  },
  {
    id: 'chat-reactions',
    label: 'Reply and react',
    group: 'Chats',
    apply: () =>
      signedIn('chats', [{ name: 'chat', id: 'dispatch' }], {
        chatUi: { dispatch: { menuFor: 'd6' } },
      }),
  },
  {
    id: 'chat-reply',
    label: 'Replying',
    group: 'Chats',
    apply: () =>
      signedIn('chats', [{ name: 'chat', id: 'jonas' }], {
        chatUi: { jonas: { replyTo: 'j4' } },
        drafts: { jonas: 'Nice! Can we use this for the' },
      }),
  },
  {
    id: 'chat-attach',
    label: 'Attachments',
    group: 'Chats',
    apply: () =>
      signedIn('chats', [{ name: 'chat', id: 'elbe' }], { chatUi: { elbe: { attachOpen: true } } }),
  },
  {
    id: 'chat-voice',
    label: 'Recording voice',
    group: 'Chats',
    apply: () =>
      signedIn('chats', [{ name: 'chat', id: 'jonas' }], {
        chatUi: { jonas: { recording: true, recordStart: Date.now() - 14000 } },
      }),
  },
  {
    id: 'chat-external',
    label: 'Cross-company group',
    group: 'Chats',
    apply: () => signedIn('chats', [{ name: 'chat', id: 'elbe' }]),
  },
  {
    id: 'new-chat',
    label: 'New chat',
    group: 'Chats',
    apply: () => signedIn('chats', [{ name: 'newChat' }]),
  },
  {
    id: 'call-incoming',
    label: 'Incoming call',
    group: 'Calls',
    apply: () =>
      signedIn('chats', [], {
        call: {
          phase: 'incoming',
          with: ['jonas'],
          video: true,
          muted: false,
          cameraOff: false,
          speaker: true,
          startedAt: Date.now(),
          minimized: false,
        },
      }),
  },
  {
    id: 'call-voice',
    label: 'In call (voice)',
    group: 'Calls',
    apply: () =>
      signedIn('chats', [{ name: 'chat', id: 'jonas' }], {
        call: {
          phase: 'active',
          with: ['miriam'],
          video: false,
          muted: false,
          cameraOff: true,
          speaker: false,
          startedAt: Date.now() - 134000,
          minimized: false,
        },
      }),
  },
  {
    id: 'call-video',
    label: 'In call (video)',
    group: 'Calls',
    apply: () =>
      signedIn('chats', [{ name: 'chat', id: 'jonas' }], {
        call: {
          phase: 'active',
          with: ['jonas'],
          video: true,
          muted: false,
          cameraOff: false,
          speaker: true,
          startedAt: Date.now() - 312000,
          minimized: false,
        },
      }),
  },
  {
    id: 'call-group',
    label: 'Group call',
    group: 'Calls',
    apply: () =>
      signedIn('chats', [{ name: 'chat', id: 'dispatch' }], {
        call: {
          phase: 'active',
          with: ['jonas', 'lea', 'felix', 'mehmet', 'jana'],
          groupName: 'Dispatch Team',
          video: true,
          muted: true,
          cameraOff: false,
          speaker: true,
          startedAt: Date.now() - 540000,
          minimized: false,
        },
      }),
  },
  { id: 'calls', label: 'Call history', group: 'Calls', apply: () => signedIn('calls') },
  {
    id: 'feed',
    label: 'Feed timeline',
    group: 'Feed',
    apply: () => signedIn('feed', [], { feedTab: 'forYou' }),
  },
  {
    id: 'post',
    label: 'Post detail',
    group: 'Feed',
    apply: () => signedIn('feed', [{ name: 'post', id: 'f1' }]),
  },
  {
    id: 'compose',
    label: 'Compose post',
    group: 'Feed',
    apply: () => signedIn('feed', [], { composeOpen: true }),
  },
  { id: 'you', label: 'Profile', group: 'You', apply: () => signedIn('you') },
  {
    id: 'you-app-lock-off',
    label: 'App lock reminder',
    group: 'You',
    apply: () => ({
      ...signedIn('you'),
      orgId: 'praxis',
      auth: { stage: 'in', email: 'm.falk@praxis-am-park.de' },
      onboarding: { ...getState().onboarding, unlock: null },
    }),
  },
  {
    id: 'settings',
    label: 'Privacy settings',
    group: 'You',
    apply: () => signedIn('you', [{ name: 'settings', page: 'privacy' }]),
  },
  {
    id: 'recovery',
    label: 'Recovery key',
    group: 'You',
    apply: () => signedIn('you', [{ name: 'settings', page: 'recovery' }]),
  },
  {
    id: 'devices',
    label: 'Linked devices',
    group: 'You',
    apply: () => signedIn('you', [{ name: 'settings', page: 'devices' }]),
  },
];

export function applyPreset(id: string): void {
  const p = presets.find((x) => x.id === id);
  if (p) setState(p.apply());
}

// ---------------------------------------------------------------------------------------------
// URL hash

const LAYOUTS: Layout[] = ['phone', 'duoOuter', 'duoInner', 'duoSplit', 'desktopMac', 'desktopWin'];

export function readHash(): void {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const patch: Partial<State> = {};
  const layout = params.get('layout') as Layout | null;
  if (layout && LAYOUTS.includes(layout)) patch.layout = layout;
  const mode = params.get('mode') as Mode | null;
  if (mode === 'light' || mode === 'dark') patch.mode = mode;
  if (params.get('landscape') === '1') patch.landscape = true;
  const split = params.get('split');
  if (split === 'half' || split === 'twoThirds' || split === 'third') patch.split = split;
  const org = params.get('org');
  if (org === 'none') patch.orgId = null;
  else if (org === 'nordwind' || org === 'praxis') patch.orgId = org;
  else if (org && /^[0-9a-f]{6}$/i.test(org)) {
    patch.orgId = 'custom';
    patch.customAccent = `#${org}`;
  }
  setState(patch);
  const screen = params.get('screen');
  if (screen) applyPreset(screen);
  if (org === 'none')
    setState({ orgId: null, auth: { ...getState().auth, email: 'paul.schreiber@example.com' } });
}

export function writeHash(s: State): void {
  const params = new URLSearchParams();
  params.set('layout', s.layout);
  params.set('mode', s.mode);
  if (s.layout === 'duoInner' && s.landscape) params.set('landscape', '1');
  if (s.layout === 'duoSplit') params.set('split', s.split);
  if (s.auth.stage === 'in') {
    params.set(
      'org',
      s.orgId === null
        ? 'none'
        : s.orgId === 'custom'
          ? (s.customAccent ?? '').replace('#', '')
          : s.orgId,
    );
  }
  const next = `#${params.toString()}`;
  if (location.hash !== next) history.replaceState(null, '', next);
}
